import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { TrendingUp, DollarSign, Users, Target, Flame, Calendar, Activity, CheckCircle } from 'lucide-react';

const getMetricIcon = (metricKey) => {
  const iconMap = {
    mrr: DollarSign,
    cac: Users,
    churn: Activity,
    ltv: Target,
    burn_rate: Flame,
    runway: Calendar,
    growth: TrendingUp,
    pmf: CheckCircle
  };
  return iconMap[metricKey] || TrendingUp;
};

const getMetricInputFields = (metricKey) => {
  const fields = {
    mrr: [{key: 'monthly_revenue', label: 'Monthly Revenue ($)', type: 'number'}],
    cac: [
      {key: 'marketing_spend', label: 'Marketing Spend ($)', type: 'number'},
      {key: 'customers_acquired', label: 'Customers Acquired', type: 'number'}
    ],
    churn: [
      {key: 'churned_customers', label: 'Churned Customers', type: 'number'},
      {key: 'total_customers', label: 'Total Customers', type: 'number'}
    ],
    ltv: [
      {key: 'average_revenue_per_user', label: 'Average Revenue per User ($)', type: 'number'},
      {key: 'average_customer_lifespan', label: 'Average Customer Lifespan (months)', type: 'number'}
    ],
    burn_rate: [{key: 'monthly_expenses', label: 'Monthly Expenses ($)', type: 'number'}],
    runway: [
      {key: 'current_cash', label: 'Current Cash ($)', type: 'number'},
      {key: 'monthly_burn', label: 'Monthly Burn ($)', type: 'number'}
    ],
    growth: [
      {key: 'new_customers', label: 'Current Customers', type: 'number'},
      {key: 'previous_customers', label: 'Previous Customers', type: 'number'}
    ],
    pmf: [{key: 'very_disappointed_percentage', label: 'Very Disappointed % (Sean Ellis)', type: 'number'}]
  };
  return fields[metricKey] || [];
};

const formatValue = (metricKey, value, currency = 'USD') => {
  if (value === null || value === undefined) return 'Not calculated';
  
  const currencySymbols = {
    USD: '$', EUR: '€', GBP: '£', JPY: '¥', INR: '₹', CAD: 'C$', AUD: 'A$'
  };
  
  const symbol = currencySymbols[currency] || '$';
  
  if (metricKey === 'mrr' || metricKey === 'cac' || metricKey === 'ltv' || metricKey === 'burn_rate') {
    if (currency === 'JPY') {
      return `${symbol}${Math.round(value).toLocaleString('en-US')}`;
    }
    return `${symbol}${value.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  } else if (metricKey === 'churn' || metricKey === 'growth' || metricKey === 'pmf') {
    return `${value.toFixed(2)}%`;
  } else if (metricKey === 'runway') {
    return `${value.toFixed(1)} months`;
  }
  return value.toString();
};

const MetricCard = ({ metric, onUpdate, currency = 'USD' }) => {
  const [isFlipped, setIsFlipped] = useState(false);
  const [inputs, setInputs] = useState(metric.inputs || {});
  const Icon = getMetricIcon(metric.metric_key);
  const inputFields = getMetricInputFields(metric.metric_key);

  const handleInputChange = (key, value) => {
    setInputs({ ...inputs, [key]: parseFloat(value) || 0 });
  };

  const handleSave = () => {
    onUpdate(metric.id, inputs);
    setIsFlipped(false);
  };

  return (
    <div
      className={`metric-card ${isFlipped ? 'flipped' : ''}`}
      onClick={() => !isFlipped && setIsFlipped(true)}
      data-testid={`metric-card-${metric.metric_key}`}
    >
      <div className="metric-card-inner" style={{ minHeight: '280px' }}>
        {/* Front */}
        <div className="metric-card-front p-6 flex flex-col justify-between">
          <div>
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-600/20 rounded-xl flex items-center justify-center">
                <Icon className="w-6 h-6 text-cyan-400" />
              </div>
              {metric.calculated && (
                <div className="px-2 py-1 bg-green-500/10 text-green-400 text-xs rounded-full border border-green-500/30">
                  Calculated
                </div>
              )}
            </div>
            <h3 className="text-lg font-semibold text-slate-100 mb-2">{metric.metric_name}</h3>
            <p className="text-sm text-slate-400 mb-4">{metric.description}</p>
          </div>
          <div>
            <div className="text-3xl font-bold text-cyan-400">
              {metric.calculated ? formatValue(metric.metric_key, metric.value, currency) : 'No data'}
            </div>
            <p className="text-xs text-slate-500 mt-2">Click to {metric.calculated ? 'update' : 'add data'}</p>
          </div>
        </div>

        {/* Back */}
        <div
          className="metric-card-back p-6 overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
          data-testid={`metric-card-back-${metric.metric_key}`}
        >
          <h3 className="text-lg font-semibold text-slate-100 mb-4">Update {metric.metric_name}</h3>
          <div className="space-y-4">
            {inputFields.map(field => (
              <div key={field.key}>
                <Label htmlFor={field.key} className="text-slate-300 text-sm">{field.label}</Label>
                <Input
                  id={field.key}
                  data-testid={`input-${metric.metric_key}-${field.key}`}
                  type={field.type}
                  value={inputs[field.key] || ''}
                  onChange={(e) => handleInputChange(field.key, e.target.value)}
                  className="bg-slate-800 border-slate-700 text-slate-100 mt-1"
                />
              </div>
            ))}
            <div className="flex space-x-2 pt-2">
              <Button
                onClick={handleSave}
                data-testid={`save-metric-${metric.metric_key}`}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              >
                Save
              </Button>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFlipped(false);
                }}
                data-testid={`cancel-metric-${metric.metric_key}`}
                variant="outline"
                className="flex-1 border-slate-700 text-slate-300"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricCard;